import { useEffect, useState } from "react";
import axios from "axios";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { selectContries } from "../../../AppSlice";
import { selectText } from "../../LanguageSelect/LanguageSelectSlice";
import type {
  AddressChangeInfo,
  AddressSuggestion,
  FullAddress,
  IDynamicFieldProps,
  IErrors,
  IFeildsSection,
} from "../../Address/types";
import { MAIN_URL, notDigits, onlyDigitsWithSpaces, onlyDigts } from "../../../constants";
import { setAddress, setIsUpdatedAddress } from "../ContactInfoSlice";
import { Roles } from "../../../types/states";

export const useAddressComponent = (params: {
  setValue: UseFormSetValue<FieldValues>;
  errors: IErrors;
  setErrors(errord: IErrors): void;
}) => {
  const { setErrors, errors, setValue } = params;
  const countries = useAppSelector(selectContries);
  const text = useAppSelector(selectText);

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [addressInput, setAddressinput] = useState<string>("");
  const [loaclError, setLocalError] = useState<string | null>(null);
  const [manual, setManual] = useState<boolean>(false);
  const [fullAddress, setFullAddress] = useState<null | FullAddress>(null);
  const address_fields = getFeildInfo(text, "address_change", countries);

  useEffect(() => {
    if (fullAddress) {
      const keys = Object.keys(fullAddress);
      keys.forEach((key) => {
        setValue(key, fullAddress[key] || "");
      });
    }
  }, [fullAddress, setValue]);

  const validateAddress = async (input: string) => {
    setLocalError(null);

    axios.defaults.withCredentials = true;
    const data = await getAddressSugestions(input, text.language);

    if (!data || !data.result) {
      setManual(true);
      setLocalError(text.fill_reg_fields);
      return;
    }
    const _suggestions: AddressSuggestion[] = data.suggestions;
    if (!_suggestions.length) {
      setLocalError(text.illigble_address);
      return;
    }
    if (_suggestions.length === 1) {
      const placeId = _suggestions[0].place_id;
      const _fullAddress = await getFullAddress(placeId, text.language);
      if (!_fullAddress) {
        setLocalError(text.oops);
        return;
      }
      const isAddresValid: boolean = finalAddressValidation(_fullAddress);
      if (!isAddresValid) {
        return;
      }
      setFullAddress(_fullAddress);
      return;
    }
    setSuggestions(_suggestions.slice());
  };

  const pickSuggestion = async (suggest: AddressSuggestion) => {
    setAddressinput(suggest.description);
    const _fullAddress = await getFullAddress(suggest.place_id, text.language);
    if (!_fullAddress) {
      setLocalError(text.fill_reg_fields);
      setManual(true);
      return;
    }
    const isAddresValid: boolean = finalAddressValidation(_fullAddress);
    if (!isAddresValid) {
      return;
    }

    setFullAddress(_fullAddress);
  };

  const finalAddressValidation = (fullAddress: FullAddress): boolean => {
    const isValid = !Object.keys(fullAddress).some((field) => {
      const isValueExist = !!fullAddress[field].trim();

      if (!isValueExist && field === "country") {
        setError("address_input", true, text.illigble_address);
        return true;
      }

      return false;
    });

    return isValid;
  };

  const setError = (key: string, err: boolean, msg: string) => {
    const _errors = { ...errors };
    const error = { err, msg };
    _errors[key] = error;
    setErrors(_errors);
  };

  const updateFullAddress = (value: string, key: string) => {
    const _fullAddress = { ...fullAddress };
    _fullAddress[key] = value;
    setFullAddress(_fullAddress);
    const _errors = { ...errors };
    if (!_errors[key]) {
      return;
    }
    _errors[key].err = false;
    _errors[key].msg = "";
    setErrors(_errors);
    setAddressinput("");
  };

  return {
    manual,
    updateFullAddress,
    loaclError,
    pickSuggestion,
    validateAddress,
    setLocalError,
    suggestions,
    setSuggestions,
    fullAddress,
    setFullAddress,
    address_fields,
  };
};

export const useSubmitAddressChange = (params: {
  errors: IErrors;
  setErrors(errors: IErrors): void;
  handleClose(): void;
}) => {
  const { errors, setErrors, handleClose } = params;
  const dispatch = useAppDispatch();
  const text = useAppSelector(selectText);

  const setUpdatedAddress = (data: any) => {
    const validation = formValidation(data);
    if (!validation) {
      return;
    }

    const info: AddressChangeInfo = {
      street: data.street,
      country: data.country,
      city: data.city,
      state: data.state,
      zipcode: data.zipcode,
      building: data.building,
      apt: data.apt,
      //   regNum: regNum as number,
    };

    dispatch(setAddress(info));
    dispatch(setIsUpdatedAddress());
    handleClose();
  };

  const formValidation = (data: { [key: string]: any }) => {
    let fields_errors = { ...errors };
    Object.keys(data).forEach(function (key, index) {
      fields_errors[key] = { err: false, msg: "" };

      if (key.includes("state")) {
        if (data.country === "Canada" || data.country === "United States") {
          let value = data[key] ? !data[key].toString().trim() : true;
          let msg = value ? text.validation.req_feild : "";
          const error = { err: value, msg: msg };
          fields_errors[key] = error;
          const updatedErrors: IErrors = feildValidation(data, fields_errors, text);
          fields_errors = { ...updatedErrors };
          return;
        }
      }
      if (key.includes("apt")) {
        return;
      }
      let value = data[key] ? !data[key].toString().trim() : true;
      let msg = value ? text.validation.req_feild : "";
      const error = { err: value, msg: msg };
      fields_errors[key] = error;
    });

    const updatedErrors: IErrors = feildValidation(data, fields_errors, text);

    setErrors(updatedErrors);

    let validation: boolean = true;
    Object.keys(updatedErrors).map((key) => {
      if (updatedErrors[key].err === true) {
        validation = false;
        return;
      }
      return;
    });
    return validation;
  };

  return { setUpdatedAddress, errors, setErrors };
};

export const getFullAddress = async (place_id: string, language: string) => {
  try {
    const response = await axios.get(`${MAIN_URL}/contact_info/getFullAddress/${language}?placeId=${place_id}`);
    const fullAddress: FullAddress = response.data.fullAddress;
    return fullAddress;
  } catch (err) {
    return null;
  }
};

export const getAddressSugestions = async (
  input: string,
  language: string
): Promise<{ result: boolean; suggestions: AddressSuggestion[] } | null> => {
  try {
    const response = await axios.get(`${MAIN_URL}/contact_info/getAddressSuggestions/${language}?input=${input}`);
    const data: { result: boolean; suggestions: AddressSuggestion[] } = response.data;
    return data;
  } catch (err) {
    return null;
  }
};

export const getFeildInfo = (text: any, name: string, selectList?: string[] | Roles[]): IDynamicFieldProps[] => {
  const feilds: IFeildsSection = {
    new_user: [
      {
        type: "input",
        name: "username",
        label: "Username",
        size: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
        multiline: false,
      },
      {
        type: "select",
        name: "role",
        label: "role",
        size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 },
        multiline: false,
        values: selectList as Roles[],
      },
      {
        type: "input",
        name: "password",
        label: "Password",
        size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 },
        multiline: false,
      },
    ],
    address_change: [
      {
        type: "input",
        name: "address",
        label: text.formsFeilds.address,
        size: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
        multiline: false,
      },
      {
        type: "select",
        name: "country",
        label: text.formsFeilds.country,
        size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 },
        multiline: false,
        values: selectList,
      },
      {
        type: "input",
        name: "state",
        label: text.formsFeilds.state,
        size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 },
        multiline: false,
      },
      {
        type: "input",
        name: "city",
        label: text.formsFeilds.city,
        size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 },
        multiline: false,
      },
      {
        type: "input",
        name: "zipcode",
        label: text.formsFeilds.zipcode,
        size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 },
        multiline: false,
      },
    ],
    address_change_GAPI: [
      {
        type: "input",
        name: "address",
        label: text.formsFeilds.address,
        size: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
        multiline: false,
      },
      {
        type: "input",
        name: "country",
        label: text.formsFeilds.country,
        size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 },
        multiline: false,
        // values: contries,
      },
      {
        type: "input",
        name: "state",
        label: text.formsFeilds.state,
        size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 },
        multiline: false,
      },
      {
        type: "input",
        name: "city",
        label: text.formsFeilds.city,
        size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 },
        multiline: false,
      },
      {
        type: "input",
        name: "zipcode",
        label: text.formsFeilds.zipcode,
        size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 },
        multiline: false,
      },
    ],
    address_change_no_postal: [
      {
        type: "input",

        name: "address",
        label: text.formsFeilds.address,
        size: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
        multiline: false,
      },
      {
        type: "input",
        name: "country",
        label: text.formsFeilds.country,
        size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 },
        multiline: false,
        // values: contries,
      },
      {
        type: "input",
        name: "state",
        label: text.formsFeilds.state,
        size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 },
        multiline: false,
      },
      {
        type: "input",
        name: "city",
        label: text.formsFeilds.city,
        size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 },
        multiline: false,
      },
    ],
  };
  return feilds[name];
};

export const feildValidation = (data: { [key: string]: any }, fields_errors: IErrors, text: any) => {
  Object.keys(data).forEach(function (feildName, index) {
    if (!data[feildName] || !!!data[feildName].trim()) {
      return;
    }
    if (feildName.includes("city") || feildName.includes("state")) {
      const pattern = notDigits;
      const valueErrors = validateValue(
        pattern,
        data[feildName],
        feildName,
        fields_errors,

        text.validation.not_number
      );
      fields_errors = { ...valueErrors };
    } else if (feildName.includes("phone")) {
      const pattern = onlyDigts;
      const valueErrors = validateValue(
        pattern,
        data[feildName],
        feildName,
        fields_errors,

        text.validation.only_number
      );
      fields_errors = { ...valueErrors };
    } else if (feildName.includes("zip")) {
      const pattern = onlyDigitsWithSpaces;
      const valueErrors = validateValue(
        pattern,
        data[feildName],
        feildName,
        fields_errors,

        text.validation.digits_char
      );
      fields_errors = { ...valueErrors };
    }
  });

  return fields_errors;
};

function validateValue(regex: RegExp, value: string, feildName: string, fields_errors: IErrors, text: string): IErrors {
  const test = regex.test(value);
  const msg = test ? "" : text;
  const error = { err: !test, msg: msg };
  fields_errors[feildName] = error;

  return fields_errors;
}
